import type { VoidFunctionComponent } from 'react';

import { errorMessagesListFromResponse, type FailedFetchResponse } from '../../helpers/errors';
import { useAsyncValue, useOnMount } from '../../hooks';
import { get } from '../../redux/api';
import { icons } from '../generic';
import Accordion from '../generic/Accordion';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../generic/Button';
import Card from '../generic/Card/Card';
import Flex from '../generic/Flex';
import Loader from '../generic/Loader';
import { LoadingBoundary } from '../generic/LoadingBoundary';
import type { GetFaktoryInfoResponse } from '../../../shared/types/routes/jobs';

const FaktoryInfo: VoidFunctionComponent = () => {
  const { setPending, setReject, setResolve, ...value } = useAsyncValue<GetFaktoryInfoResponse>();

  const fetchData = async () => {
    try {
      setPending();
      const { data } = await get<GetFaktoryInfoResponse>('/api/jobs/faktory');
      if (data) {
        setResolve(data);
      }
    } catch (err) {
      setReject(errorMessagesListFromResponse(err as FailedFetchResponse));
    }
  };

  useOnMount(fetchData);

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="faktory-info" isInitiallyOpen>
          <Accordion.Target>Faktory Info</Accordion.Target>
          <Accordion.Body>
            <Button type={BUTTON_TYPE.ANCHOR} size={BUTTON_SIZE.LARGE} onClick={fetchData} icon={icons.refresh}>
              Refresh
            </Button>
            {value.errors && value.errors.map((err) => (<p>{err}</p>))}
            <Flex direction="row" justify="flex-start" align="flex-start" gap={12}>
              <Card variant="fill">
                <Card.Header>Queues:</Card.Header>
                <Card.Content>
                  <LoadingBoundary
                    asyncValue={value}
                    renderLoading={<Loader />}
                    renderValue={({ value: data }) => (
                      <>
                        {Object.entries(data.faktory.queues ?? {}).map(([key, val]) => (
                          <p>
                            - {key}: {val}
                          </p>
                        ))}
                      </>
                    )}
                    renderEmpty={<>No data</>}
                    renderError={() => <>Error.</>}
              />
                </Card.Content>
              </Card>
              <Card variant="fill">
                <Card.Header>Tasks:</Card.Header>
                <Card.Content>
                  <LoadingBoundary
                    asyncValue={value}
                    renderLoading={<Loader />}
                    renderValue={({ value: data }) => (
                      <>
                        <p>- Busy: {data.faktory?.tasks?.Busy?.size}</p>
                        <p>- Retries: {data.faktory?.tasks?.Retries?.size}</p>
                        <p>- Scheduled: {data.faktory?.tasks?.Scheduled?.size}</p>
                      </>
                    )}
                    renderEmpty={<>No data</>}
                    renderError={() => <>Error.</>}
              />
                </Card.Content>
              </Card>
              <Card variant="fill">
                <Card.Header>General Info</Card.Header>
                <Card.Content>
                  <LoadingBoundary
                    asyncValue={value}
                    renderLoading={<Loader />}
                    renderValue={({ value: data }) => (
                      <>
                        <p># of Workers: {data.faktory?.tasks?.Workers?.size}</p>
                        <p># of Connections: {data.server.connections}</p>
                        <p>Total Enqueued: {data.faktory.total_enqueued}</p>
                        <p>Failed: {data.faktory.total_failures}</p>
                        <p>Processed: {data.faktory.total_processed}</p>
                      </>
                    )}
                    renderEmpty={<>No data</>}
                    renderError={() => <>Error.</>}
              />
                </Card.Content>
              </Card>
            </Flex>
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default FaktoryInfo;
